import ShopifyLogo from '@/assets/icons/shopify.svg'
import { useAdminContext } from '@/contexts/AdminContext'
import { useAuth } from "@/contexts/AuthContext"
import { useToast } from "@/contexts/ToastContext"
import { authService, VerifyAuthResponse } from "@common/api/authService"
import Logo from "@common/components/Logo"
import LogoWithLink from '@common/components/Logo/LogoWithLink'
import { Icon } from '@common/index'
import { supabase } from '@salerio/common/src/api/authService/supabase'
import { User } from "@supabase/supabase-js"
import { useEffect, useState } from "react"
import styles from "./auth.module.scss"

const CustomAuthForm: React.FC = () => {
    const { modal } = useAdminContext();
    const { showToast } = useToast()
    const [isLogin, setIsLogin] = useState(true)
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [showForgotPassword, setShowForgotPassword] = useState(false)
    const [resetEmail, setResetEmail] = useState('')
    const [resetLoading, setResetLoading] = useState(false)
    const [resetMessage, setResetMessage] = useState('')
    const [emailError, setEmailError] = useState('')
    const [passwordError, setPasswordError] = useState('')

    // 验证邮箱格式的正则表达式
    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    // 转换 Supabase 错误为友好的错误消息
    const getFriendlyErrorMessage = (error: any): string => {
        const status = error?.status
        const message = error?.message || ''

        // 根据状态码判断
        switch (status) {
            case 400:
                if (message.includes('Invalid login credentials')) {
                    return "That password doesn't look right."
                }
                if (message.includes('Email not confirmed')) {
                    return "Please check your inbox and verify your email first."
                }
                if (message.includes('User already registered')) {
                    return "This email is already in use. Try signing in instead."
                }
                if (message.includes('Password should be')) {
                    return "That password is too weak. Try something stronger."
                }
                return "Something doesn't look right. Please check and try again."

            case 409:
                return "This email is already in use. Try signing in instead."

            case 422:
                return "That password is too weak. Try something stronger."

            case 429:
                return "Whoa, slow down! Give it a moment and try again."

            case 500:
            case 503:
                return "Our servers are having a moment. Please try again later."

            default:
                // 如果没有状态码，回退到消息匹配
                if (message.includes('Invalid login credentials')) {
                    return "That password doesn't look right."
                }
                if (message.includes('Email not confirmed')) {
                    return "Please check your inbox and verify your email first."
                }
                if (message.includes('User already registered')) {
                    return "This email is already in use. Try signing in instead."
                }
                if (message.includes('signups are disabled')) {
                    return "Signing up is currently unavailable."
                }
                if (message.includes('rate limit') || message.includes('too many requests')) {
                    return "Whoa, slow down! Give it a moment and try again."
                }
                if (message.includes('Failed to fetch') || message.includes('Network')) {
                    return "Having trouble connecting. Check your internet and try again."
                }
                return message
        }
    }

    const handleEmailAuth = async (e: React.FormEvent) => {
        e.preventDefault()

        // 验证邮箱
        if (!email) {
            setEmailError('Please enter your email address')
            return
        } else if (!isValidEmail(email)) {
            setEmailError('Please enter a valid email address')
            return
        } else {
            setEmailError('')
        }

        // 验证密码
        if (!password) {
            setPasswordError('Please enter your password')
            return
        } else {
            setPasswordError('')
        }

        setLoading(true)
        setError('')
        try {
            if (isLogin) {
                const { error } = await supabase.auth.signInWithPassword({
                    email,
                    password
                })
                if (error) throw error
            } else {
                const { error, data } = await supabase.auth.signUp({
                    email,
                    password,
                    options: {emailRedirectTo: `${window.location.origin}/admin`}
                })
                if (error) throw error
                if(data.user?.identities?.length === 0){
                    throw new Error('The email has already been signed up')
                }

                showToast('Check your email for verification link', 'success')
            }
        } catch (error: any) {
            setError(getFriendlyErrorMessage(error))
        } finally {
            setLoading(false)
        }
    }

    const handleGoogleAuth = async () => {
        try {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {redirectTo: `${window.location.origin}/admin`}
            })
            if (error) throw error
        } catch (error: any) {
            const friendlyError = getFriendlyErrorMessage(error)
            setError(friendlyError)
            showToast(friendlyError, 'error')
        }
    }
    
    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault()
        setResetLoading(true)
        setError('')
        setResetMessage('')

        try {
            const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {redirectTo: `${window.location.origin}/admin`,})
            if (error) throw error
            setResetMessage('Password reset email sent! Please check your inbox.')
            showToast('Password reset email sent!', 'success')
        } catch (error: any) {
            const friendlyError = getFriendlyErrorMessage(error)
            setError(friendlyError)
            showToast(friendlyError, 'error')
        } finally {
            setResetLoading(false)
        }
    }
    
    const handleShopifyAuth = () => {
        modal.openModal('connectShopify')
    }
    
    return (
        <div className={styles.customAuthForm}>
            <div className={styles.oauthButtons}>
                <button 
                    type="button"
                    onClick={handleGoogleAuth}
                    className={styles.googleButton}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    Sign in with Google
                </button>
                {/*                 
                <button 
                    type="button"
                    onClick={handleShopifyAuth}
                    className={styles.shopifyButton}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M16.3486 3.89409C16.3353 3.79357 16.2508 3.73794 16.1809 3.73181C16.1111 3.72575 14.6347 3.61181 14.6347 3.61181C14.6347 3.61181 13.6093 2.5519 13.4968 2.43459C13.3842 2.31734 13.1643 2.353 13.0789 2.37916C13.0663 2.38302 12.8549 2.45097 12.5051 2.56368C12.1626 1.53743 11.5581 0.594361 10.4946 0.594361C10.4652 0.594361 10.435 0.5956 10.4048 0.59739C10.1024 0.180931 9.72771 0 9.4041 0C6.92679 0 5.74325 3.22448 5.37217 4.86305C4.40954 5.17362 3.7257 5.39441 3.63835 5.42298C3.10103 5.59848 3.08404 5.6161 3.01348 6.14327C2.96039 6.54238 1.55447 17.8629 1.55447 17.8629L12.5096 20.0001L18.4455 18.6631C18.4455 18.6631 16.3617 3.9946 16.3486 3.89409ZM11.8996 2.75865L10.9726 3.05738C10.9729 2.98936 10.9733 2.92244 10.9733 2.84926C10.9733 2.21145 10.8882 1.69792 10.7518 1.29082C11.2998 1.36242 11.6647 2.01159 11.8996 2.75865ZM10.0721 1.4173C10.2244 1.81475 10.3235 2.38515 10.3235 3.15487C10.3235 3.19425 10.3231 3.23026 10.3228 3.26668C9.71997 3.4611 9.06489 3.67219 8.40835 3.88397C8.77699 2.40264 9.46797 1.68718 10.0721 1.4173ZM9.33606 0.691849C9.44298 0.691849 9.55069 0.729646 9.65378 0.80352C8.85984 1.19251 8.00883 2.17221 7.64945 4.12865L6.13616 4.61664C6.5571 3.12437 7.55668 0.691849 9.33606 0.691849Z" fill="#95BF46" />
                        <path d="M16.1809 3.73181C16.1111 3.72575 14.6347 3.61181 14.6347 3.61181C14.6347 3.61181 13.6093 2.5519 13.4968 2.43459C13.4547 2.39094 13.3979 2.36862 13.3385 2.35898L12.5096 20.0001L18.4455 18.6631C18.4455 18.6631 16.3617 3.9946 16.3486 3.89409C16.3353 3.79357 16.2508 3.73794 16.1809 3.73181Z" fill="#5E8E3E" />
                        <path d="M10.4946 7.14685L9.76269 9.41372C9.76269 9.41372 9.12143 9.05737 8.33536 9.05737C7.18296 9.05737 7.12497 9.81036 7.12497 10.0001C7.12497 11.0354 9.717 11.4321 9.717 13.8572C9.717 15.7652 8.55475 16.9938 6.98763 16.9938C5.10709 16.9938 4.14539 15.7752 4.14539 15.7752L4.64892 14.043C4.64892 14.043 5.63746 14.9266 6.47161 14.9266C7.01666 14.9266 7.23837 14.4798 7.23837 14.1533C7.23837 12.8028 5.11185 12.7426 5.11185 10.5234C5.11185 8.65564 6.3994 6.84819 8.99844 6.84819C9.99988 6.84819 10.4946 7.14685 10.4946 7.14685Z" fill="white" />
                    </svg>
                    Sign in with Shopify
                </button> */}
            </div>
            
            <div className={styles.divider}>
                <span>or</span>
            </div>
            
            <form onSubmit={handleEmailAuth} className={styles.form} noValidate>
                <div className={styles.inputGroup}>
                    <label htmlFor="email">Email address</label>
                    <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value)
                            if (emailError) setEmailError('')
                        }}
                        placeholder="Enter your email"
                        style={{border: emailError ? '1px solid red' : undefined}}
                    />
                    {emailError ? (
                        <div
                            style={{
                                color: 'red', fontSize: '14px', marginTop: '4px'
                            }}
                        >
                            {emailError}
                        </div>
                    ) : null}
                </div>

                <div className={styles.inputGroup}>
                    <div className={styles.passwordLabelRow}>
                        <label htmlFor="password">Password</label>
                        <button
                            type="button"
                            onClick={() => {
                                setShowForgotPassword(true)
                                setResetEmail(email)
                            }}
                            className={styles.linkButton}
                        >
                            Forgot Password?
                        </button>
                    </div>
                    <div className={styles.passwordInputWrapper}>
                        <input
                            id="password"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value)
                                if (passwordError) setPasswordError('')
                            }}
                            placeholder="Enter your password"
                            style={{border: passwordError ? '1px solid red' : undefined}}
                        />
                        <button
                            type="button"
                            className={styles.passwordToggle}
                            onClick={() => setShowPassword(!showPassword)}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                            {!showPassword ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                </svg>
                            ) : (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                            )}
                        </button>
                    </div>
                    {passwordError ? (
                        <div
                            style={{
                                color: 'red', fontSize: '14px', marginTop: '4px'
                            }}
                        >
                            {passwordError}
                        </div>
                    ) : null}
                </div>
                 
                {error ? (
                    <div
                        style={{color: 'red', fontSize: '14px'}}
                    >
                        {error}
                    </div>
                ) : null}
                
                <button 
                    type="submit" 
                    className={styles.primaryButton}
                    disabled={loading}
                >
                    {loading ? 'Loading...' : (isLogin ? 'Sign In' : 'Sign Up')}
                </button>
            </form>
            
            
            <div className={styles.toggleMode}>
                <button 
                    type="button"
                    onClick={() => setIsLogin(!isLogin)}
                    className={styles.linkButton}
                >
                    {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
                </button>
            </div>

            {showForgotPassword ? (
                <div className={styles.forgotPasswordModal}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h3>Reset Password</h3>
                            <button 
                                type="button" 
                                onClick={() => {
                                    setShowForgotPassword(false)
                                    setResetEmail('')
                                    setResetMessage('')
                                    setError('')
                                }}
                                className={styles.closeButton}
                            >
                                ×
                            </button>
                        </div>
                        
                        <form onSubmit={handleForgotPassword} className={styles.resetForm}>
                            <div className={styles.inputGroup}>
                                <label htmlFor="resetEmail">Email</label>
                                <input
                                    id="resetEmail"
                                    type="email"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    required
                                    placeholder="Enter your email address"
                                />
                            </div>
                            
                            {resetMessage ? (
                                <div
                                    style={{
                                        color: 'green', fontSize: '14px', marginTop: '8px' 
                                    }}
                                >
                                    {resetMessage}
                                </div>
                            ) : null}
                            {error ? (
                                <div
                                    style={{
                                        color: 'red', fontSize: '14px', marginTop: '8px' 
                                    }}
                                >
                                    {error}
                                </div>
                            ) : null}
                            
                            <button type="submit" disabled={resetLoading} className={styles.primaryButton}>
                                {resetLoading ? 'Sending...' : 'Reset'}
                            </button>
                        </form>
                    </div>
                </div>
            ) : null}
        </div>
    )
}

export default function AuthPage() {
    const searchParams = new URLSearchParams(location.search);
    const connect = searchParams.get('connect');
    const platformId = searchParams.get('platformId');
    const platformUserName = searchParams.get('platformUserName');
    const redirect = searchParams.get('redirect');
    const { user ,profile } = useAuth();

    function ConnectShopifyStatus({
        platformId, platformUserName, user, redirect,profile 
    }: { platformId: string, platformUserName: string, user: User, redirect: string,profile: VerifyAuthResponse }) {
        const [loading, setLoading] = useState(false);
        const { showToast } = useToast();
        useEffect(() => {
            location.href = decodeURIComponent(redirect || '/admin');   
        }, [profile, platformUserName, redirect])

        const handleConfirm = async () => {
            try {
                setLoading(true);
                const response = await authService.bindShop({
                    accessToken: platformId,
                    domain: platformUserName
                });

                if (response === 'success') {
                // Redirect to homepage after successful binding
                    location.href = decodeURIComponent(redirect || '/admin');
                } else {
                    showToast('An error occurred during connection', 'error');
                }
            } catch (error) {
                showToast('An error occurred during connection', 'error');
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className={styles.connectContainer}>
                <div className={styles.side}>
                    <Logo style={{ height: 40 }} />
                    <span className={styles.label}>{user?.user_metadata?.full_name}</span>
                </div>
                <div className={styles.center}>
                    <span className={styles.line}></span>
                </div>
                <div className={styles.side}>
                    <img src={ShopifyLogo} alt="Shopify" className={styles.shopifyLogo} />
                    <span className={styles.label}>{platformUserName}</span>
                </div>
                <button
                    className={styles.confirmButton}
                    style={{ margin: "auto" }}
                    onClick={handleConfirm}
                    disabled={loading}
                >
                    {loading ? 'Connecting...' : 'Connect Store'}
                </button>
            </div>
        )
    }

    if (connect && platformId && platformUserName && user && profile) {
        return <ConnectShopifyStatus profile={profile} platformId={platformId} platformUserName={platformUserName} user={user} redirect={redirect || '/admin'} />
    }

    useEffect(() => {
        if (!connect && user) {
            location.href = decodeURIComponent(redirect || '/admin');
        }
    }, [connect, user, redirect])
    
    return (
        <div className={styles.container}>
            {/* content */}
            <div className={styles.content}>
                <div className={styles.authLogo}>
                    <LogoWithLink size="26px" />
                </div>
                <div className={styles.auth}>
                    <div className={styles.form}>
                        <div className={styles.description}>
                            <Icon name="logo" size={24} />
                            <p className={styles.info}>Sign in or Sign up</p>
                        </div>
                        <CustomAuthForm />
                    </div>
                </div>
            </div>
        </div>
    )
}
